import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Layout from 'components/layout/Layout'
import BlogArticle from 'features/blog/article/BlogArticle'
import { shuffleArray } from 'utils/helpers'
import { DEFAULT_NAMESPACES } from 'constants/i18n'
import type { SeoProps } from 'components/seo/types'
import { LocalizationModel } from 'types/cms/components'
import { Localizations } from 'types/language'
import { routes } from 'utils/routes'
import { cmsLib } from 'lib'
import { BlogArticleProps } from 'features/blog/article/types'

const BlogArticlePage = (props: BlogArticleProps) => {
  // Constants
  const { seo, title, description } = props.article.attributes
  const SEO: SeoProps = seo && {
    description: seo.metaDescription || description,
    image: seo?.seoImage?.data?.attributes?.url,
    title: seo.seoTitle || title
  }

  return (
    <Layout seo={SEO} localized={props.localizations}>
      <BlogArticle {...props} />
    </Layout>
  )
}

export const getStaticProps = async ({ params, locale }) => {
  // Get the slug from the params
  const { article: slug } = params

  // Get the article and all articles
  const [article, allArticles] = await Promise.all([cmsLib.getBlogArticleBySlug(slug, locale), cmsLib.getBlogArticles(locale)])

  // If the article is not found, return a 404
  if (!article) {
    return {
      notFound: true,
      revalidate: 5
    }
  }

  // Shuffle, slice & filter articles
  const shuffledArticles = shuffleArray(allArticles)
  const articles = shuffledArticles.slice(0, 5).filter((article) => article?.slug !== slug)

  // Create the localizations
  const localizations: Localizations = article.attributes.localizations?.data.reduce(
    (prev: Localizations, curr: LocalizationModel) => ({
      ...prev,
      [curr.attributes.locale]: routes(curr.attributes.locale, curr.attributes.slug).blogArticle
    }),
    { [locale]: routes(locale, slug).blogArticle } as Localizations
  )

  return {
    props: {
      article,
      localizations,
      otherArticles: articles,
      ...(await serverSideTranslations(locale, [...DEFAULT_NAMESPACES, 'blog', 'encyclopedia']))
    }
  }
}

export const getStaticPaths = async () => {
  const articles = await cmsLib.getBlogArticles('all')
  const paths = articles.map((article) => [{ locale: article.attributes.locale, params: { article: article.attributes.slug } }]).flat()

  return {
    fallback: 'blocking',
    paths // will server-render pages on-demand if the path does not exist
  }
}

export default BlogArticlePage
